import { normalizeSku } from './normalizeSku';

/** A pick row that can contribute units to a box-size SKU mix. */
export interface PackageCompositionLine {
  readonly sku: string;
  readonly isBundleParent?: boolean;
  readonly isAdjustedQty?: boolean;
  readonly quantity?: number;
  readonly name?: string;
}

const UNITS_IN_NAME = /\((\d+)\s*units?\)\s*$/i;

/** Same unit rule as pickLineUnitCount: expanded chips count as 1; adjusted-qty chips use quantity or `(N units)`. */
function compositionUnitCount(item: PackageCompositionLine): number {
  if (item.isAdjustedQty !== true) {
    return 1;
  }
  if (typeof item.quantity === 'number' && Number.isFinite(item.quantity) && item.quantity > 0) {
    return item.quantity;
  }
  const match = item.name?.match(UNITS_IN_NAME);
  if (!match) {
    return 1;
  }
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/**
 * SKUs that go into a box, one entry per unit. Bundle parents are omitted.
 * Result is normalized and sorted so line order does not change the mix.
 */
export function packageCompositionSkus(lines: readonly PackageCompositionLine[]): string[] {
  const skus: string[] = [];
  for (const line of lines) {
    if (line.isBundleParent === true) {
      continue;
    }
    const sku = normalizeSku(line.sku);
    if (!sku) {
      continue;
    }
    const count = compositionUnitCount(line);
    for (let index = 0; index < count; index += 1) {
      skus.push(sku);
    }
  }
  skus.sort();
  return skus;
}

/** Canonical mix string. The server hashes this; callers do not. */
export function packageSkuCanonical(skus: readonly string[]): string {
  return skus.map((sku) => normalizeSku(sku)).filter((sku) => sku.length > 0).sort().join(', ');
}
